
import type { Locale, Market, MarketConfig } from '@packages/locale/types/locales.ts'

type AirportInfo = {
  airport: string;
  airport_name: string
}

const LOCALES = ['en', 'no', 'sv', 'pl', 'zh-cn', 'zz']

const Markets: Record<string, Market> = {
  SWEDISH: 'sv',
  NORWEGIAN: 'no',
  DANISH: 'da',
  ENGLISH: 'en',
  SOLRESOR: 'zz',
  SOLRESOR_TEST: 'zt',
  ROLFS_TEST: 'xx',
}

type LocaleKey = 'SV' | 'NO' | 'DA' | 'EN' | 'PL' | 'ZH_CN' | 'ZZ'

const LOCALE: Record<LocaleKey, Locale> = {
  SV: 'sv',
  NO: 'no',
  DA: 'da',
  EN: 'en',
  PL: 'pl',
  ZH_CN: 'zh-cn',
  ZZ: 'zz',
}

const APP_LOCALES: readonly Locale[] = [LOCALE.SV, LOCALE.NO, LOCALE.DA, LOCALE.EN]

const MARKETS = {
  sv: {
    market: Markets.SWEDISH,
    name: 'Rolfsbuss',
    appId: 'se.rolfsbuss',
    appName: 'Rolfsresor',
    appStoreId: '6547860913',
    themeColor: '#004b9c',
    languages: [LOCALE.SV],
    isCharter: false,
    authChannel: 'bankid',
    eidMethod: 'BankID',
    watermark: {
      width: 240,
      file: 'rolfsbuss.jpg',
    },
    phoneDialCode: 46,
    urlScheme: 'rolfsbuss',
  },
  no: {
    market: Markets.NORWEGIAN,
    name: 'Rolfsreiser',
    appId: 'no.rolfsreiser',
    appName: 'Rolfsreiser',
    themeColor: '#004b9c',
    languages: [LOCALE.NO],
    isCharter: false,
    authChannel: 'sms',
    eidMethod: 'BankID',
    watermark: {
      width: 420,
      file: 'rolfsreiser.jpg',
    },
    phoneDialCode: 47,
    urlScheme: 'rolfsreiser',
  },
  da: {
    market: Markets.DANISH,
    name: 'Rolfsrejser',
    appId: 'dk.rolfsrejser',
    appName: 'Rolfsrejser',
    themeColor: '#004b9c',
    languages: [LOCALE.DA],
    isCharter: false,
    authChannel: 'sms',
    eidMethod: 'MitID',
    watermark: {
      width: 420,
      file: 'rolfsrejser.jpg',
    },
    phoneDialCode: 45,
    urlScheme: 'rolfsrejser',
  },
  en: {
    market: Markets.ENGLISH,
    name: 'Rolfstours',
    appId: 'com.rolfstours',
    appName: 'Rolfstours',
    themeColor: '#004b9c',
    languages: [LOCALE.EN],
    isCharter: false,
    authChannel: 'bankid',
    eidMethod: 'BankID',
    watermark: {
      width: 420,
      file: 'rolfstours.jpg',
    },
    phoneDialCode: 1,
    urlScheme: 'rolfstours',
  },
  zz: {
    market: Markets.SOLRESOR,
    name: 'Solresor',
    appId: 'solresor.se',
    appName: 'Solresor',
    appStoreId: '6503487454',
    themeColor: '#fecb21',
    languages: [LOCALE.ZZ],
    isCharter: true,
    authChannel: 'bankid',
    eidMethod: 'BankID',
    watermark: {
      width: 240,
      file: 'solresor.png',
    },
    phoneDialCode: 46,
    urlScheme: 'solresor',
  },
} satisfies Partial<Record<Market, MarketConfig>>

const CHARTER_MARKETS: Market[] = [Markets.SOLRESOR]

const BASE_URLS_PER_LOCALE: Partial<Record<Market, string>> = {
  sv: 'https://www.rolfsbuss.se',
  da: 'https://www.rolfsrejser.dk',
  no: 'https://www.rolfsreiser.no',
  zz: 'https://www.solresor.se',
  en: 'https://www.rolfstours.com',
}

const LOCALES_DEFAULT_AIRPORT: Record<Market, AirportInfo> = {
  sv: {
    airport: 'GOT',
    airport_name: 'Landvetter',
  },
  no: {
    airport: 'OSL',
    airport_name: 'Gardemoen',
  },
  zz: {
    airport: 'STO',
    airport_name: 'Arlanda',
  },
  da: {
    airport: 'CPH',
    airport_name: 'Kastrup',
  },
  en: {
    airport: 'LHR',
    airport_name: 'Heathrow',
  },
  zt: {
    airport: 'STO',
    airport_name: 'Arlanda',
  },
  xx: {
    airport: 'STO',
    airport_name: 'Arlanda',
  },
}

/**
 * Markets whose mobile app is released in App Store and Google Play.
 * Not to be confused with `APP_LOCALES`, which is the set of languages switchable
 * inside the app — the Rolfsreiser and Rolfsrejser apps exist in `src/applications`
 * but are not published in any store yet, and there is no Rolfstours app at all.
 */
const LOCALES_WITH_PUBLISHED_APP: readonly Market[] = ['sv', 'zz']

const LOCALES_WITH_LOGIN: readonly Market[] = ['sv']

const LOCALES_WITH_FAVOURITES: readonly Market[] = ['sv', 'zz']

const LOCALES_WITH_CHARTER_PACKAGES: readonly Market[] = ['sv', 'zz']

const LOCALES_WITH_AIRPORT_SPECIFIC_NEWSLETTER: readonly Market[] = ['sv', 'zz']

const LOCALES_WITHOUT_CONNECTIONS: readonly Market[] = ['en', 'zz']

const LOCALES_WITH_CONNECTIONS_PAGE: readonly Market[] = ['sv', 'no']

const LOCALES_WITH_BLOG: readonly Market[] = ['sv', 'no']

const LOCALES_WITH_VERCEL_FEATURES: readonly Market[] = ['sv', 'zz']

const LOCALES_WITH_ONLY_FLIGHT: readonly Market[] = ['en', 'da']

const LOCALES_WITH_NEWSLETTER_POPUP: readonly Market[] = ['no']

const LOCALES_WITH_MARKET_ONLY_FILTER: readonly Market[] = ['no', 'da']

const SUNDAY_FIRST_LOCALES: readonly Market[] = ['en']

export {
  LOCALES,
  Markets,
  LOCALE,
  MARKETS,
  CHARTER_MARKETS,
  APP_LOCALES,
  LOCALES_DEFAULT_AIRPORT,
  LOCALES_WITH_PUBLISHED_APP,
  LOCALES_WITH_LOGIN,
  LOCALES_WITH_FAVOURITES,
  LOCALES_WITH_CHARTER_PACKAGES,
  LOCALES_WITH_AIRPORT_SPECIFIC_NEWSLETTER,
  LOCALES_WITHOUT_CONNECTIONS,
  LOCALES_WITH_CONNECTIONS_PAGE,
  LOCALES_WITH_BLOG,
  LOCALES_WITH_VERCEL_FEATURES,
  LOCALES_WITH_ONLY_FLIGHT,
  LOCALES_WITH_NEWSLETTER_POPUP,
  LOCALES_WITH_MARKET_ONLY_FILTER,
  BASE_URLS_PER_LOCALE,
  SUNDAY_FIRST_LOCALES,
}
